import React, { useEffect, useMemo, useState } from "react";
import ReactEChartsCore from "echarts-for-react/lib/core";
import * as echarts from "echarts/core";
import { HOUR, renderPercentChange } from "./helper";
import AovSelectOption from "./AovSelectOption";
import { fourPlaceHolder } from "modules/utilities/utility";
import { size } from "lodash";

function AovDashboard({ loading, todayFormData, yestFormData }) {
  const [gender, setGender] = useState(null);
  const [orderType, setOrderType] = useState(null);
  const [graphType, setGraphType] = useState("total_order_value");

  const calculateSum = (array, dataLength = 24) => {
    return array
      .slice(0, dataLength)
      .reduce((sum, value) => sum + Number(value), 0)
      .toFixed(2);
  };

  const graphData = useMemo(() => {
    if (gender && graphType && orderType) {
      let genderType = gender.toLowerCase();
      const newOrRepeat = orderType;
      const key = `${genderType}${newOrRepeat}${capitalizeGraphType(graphType)}`;
      const sumKey = `${genderType}${newOrRepeat}TotalValue`;
      const todaySum = calculateSum(todayFormData[sumKey]);
      const yesterdaySum = calculateSum(yestFormData[sumKey], size(todayFormData[sumKey]));
      //count Key
      const countKey = `${genderType}${newOrRepeat}OrderCount`;
      const todayCount = calculateSum(todayFormData[countKey]);
      const yesterdayCount = calculateSum(yestFormData[countKey], size(todayFormData[countKey]));
      //Avg Key
      const aovKey = `${genderType}${newOrRepeat}Aov`;
      const todayAov = calculateSum(todayFormData[aovKey]);
      const yesterdayAov = calculateSum(yestFormData[aovKey], size(todayFormData[aovKey]));
      return {
        today: todayFormData[key],
        yesterday: yestFormData[key],
        todaySum,
        yesterdaySum,
        todayCount,
        yesterdayCount,
        todayAov,
        yesterdayAov,
      };
    } else if (gender && graphType) {
      let genderType = gender.toLowerCase();
      const key = `${genderType}${capitalizeGraphType(graphType)}`;
      const sumKey = `${genderType}TotalValue`;
      const todaySum = calculateSum(todayFormData[sumKey]);
      const yesterdaySum = calculateSum(yestFormData[sumKey], size(todayFormData[sumKey]));
      //count Key
      const countKey = `${genderType}OrderCount`;
      const todayCount = calculateSum(todayFormData[countKey]);
      const yesterdayCount = calculateSum(yestFormData[countKey], size(todayFormData[countKey]));
      //Avg key
      const aovKey = `${genderType}Aov`;
      const todayAov = calculateSum(todayFormData[aovKey]);
      const yesterdayAov = calculateSum(yestFormData[aovKey], size(todayFormData[aovKey]));
      return {
        today: todayFormData[key],
        yesterday: yestFormData[key],
        todaySum,
        yesterdaySum,
        todayCount,
        yesterdayCount,
        todayAov,
        yesterdayAov,
      };
    } else if (orderType) {
      const key = `${orderType}TotalValue`;
      const todaySum = calculateSum(todayFormData[key]);
      const yesterdaySum = calculateSum(yestFormData[key], size(todayFormData[key]));
      //count Key
      const countKey = `${orderType}OrderCount`;
      const todayCount = calculateSum(todayFormData[countKey]);
      const yesterdayCount = calculateSum(yestFormData[countKey], size(todayFormData[countKey]));
      //Avg
      const aovKey = `${orderType}Aov`;
      const todayAov = calculateSum(todayFormData[aovKey]);
      const yesterdayAov = calculateSum(yestFormData[aovKey], size(todayFormData[aovKey]));
      return {
        today: todayFormData[key],
        yesterday: yestFormData[key],
        todaySum,
        yesterdaySum,
        todayCount,
        yesterdayCount,
        todayAov,
        yesterdayAov,
      };
    } else if (graphType) {
      const todayCount = calculateSum(todayFormData.totalOrderCount);
      const yesterdayCount = calculateSum(yestFormData.totalOrderCount, size(todayFormData.totalOrderCount));
      const todayAov = calculateSum(todayFormData.avgOrderValue);
      const yesterdayAov = calculateSum(yestFormData.avgOrderValue, size(todayFormData.avgOrderValue));
      const todaySum = calculateSum(todayFormData.totalOrderValue);
      const yesterdaySum = calculateSum(yestFormData.totalOrderValue, size(todayFormData.totalOrderValue));

      if (graphType == "total_order_value") {
        return {
          today: todayFormData.totalOrderValue,
          yesterday: yestFormData.totalOrderValue,
          todaySum,
          yesterdaySum,
          todayCount,
          yesterdayCount,
          todayAov,
          yesterdayAov,
        };
      } else if (graphType == "aov") {
        return {
          today: todayFormData.avgOrderValue,
          yesterday: yestFormData.avgOrderValue,
          todaySum,
          yesterdaySum,
          todayCount,
          yesterdayCount,
          todayAov,
          yesterdayAov,
        };
      } else if (graphType == "total_order_count") {
        return {
          today: todayFormData.totalOrderCount,
          yesterday: yestFormData.totalOrderCount,
          todaySum,
          yesterdaySum,
          todayCount,
          yesterdayCount,
          todayAov,
          yesterdayAov,
        };
      } else {
        return { today: [], yesterday: [] };
      }
    }
    return [];
  }, [gender, graphType, orderType, todayFormData, yestFormData]);

  // Helper function to capitalize graphType for mapping
  function capitalizeGraphType(type) {
    switch (type) {
      case "aov":
        return "Aov";
      case "total_order_value":
        return "TotalValue";
      case "total_order_count":
        return "OrderCount";
      default:
        return "";
    }
  }

  const getOption = (data1, data2) => ({
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "line",
        lineStyle: {
          color: "#b0b0b0", // Grey pointer line
          width: 1,
        },
      },
      padding: [7, 10],
      backgroundColor: "#ffffff", // White tooltip background
      borderColor: "#b0b0b0", // Grey border
      borderWidth: 1,
      textStyle: {
        color: "#000000", // Black text
        fontSize: 12,
      },
      formatter: (params) => {
        console.log("I am data1 ", data1, data2);
        const todayValue = data1[params[0].dataIndex];
        const yesterdayValue = data2[params[0].dataIndex];
        return `<b>${params[0].axisValue}</b><br/>
          <span style="color:black;">● Today:</span> ${todayValue}<br/>
          <span style="color:black;">● Yesterday:</span> ${yesterdayValue}`;
      },
    },
    xAxis: {
      type: "category",
      data: HOUR,
      boundaryGap: false,
      splitLine: {
        show: false, // No vertical grid lines
      },
      axisLine: {
        lineStyle: {
          color: "#b0b0b0", // Light grey for axis
        },
      },
      axisLabel: {
        color: "#000000", // Black labels
        fontSize: 12,
        margin: 10,
      },
    },
    yAxis: {
      type: "value",
      splitLine: {
        show: true,
        lineStyle: {
          color: "#e0e0e0", // Light grey horizontal grid lines
        },
      },
      axisLine: { show: false }, // No visible y-axis line
      axisTick: { show: false },
      axisLabel: {
        color: "#000000", // Black labels
        fontSize: 12,
      },
    },
    series: [
      {
        name: "Today",
        type: "line",
        smooth: true,
        data: data1,
        symbol: "circle", // Circle points
        symbolSize: 6,
        itemStyle: {
          color: "#000000", // Black points
        },
        lineStyle: {
          color: "#000000", // Solid black line
          width: 2,
        },
      },
      {
        name: "Yesterday",
        type: "line",
        smooth: true,
        data: data2,
        symbol: "circle", // Circle points
        symbolSize: 6,
        itemStyle: {
          color: "#000000", // Black points
        },
        lineStyle: {
          color: "#000000", // Black dotted line
          width: 2,
          type: "dotted",
        },
      },
    ],
    grid: {
      left: "5%",
      right: "5%",
      bottom: "10%",
      top: "5%", // Clean margins for the graph
      containLabel: true,
    },
  });

  const todayorderSum = graphData.todaySum;
  const yesterdayorderSum = graphData.yesterdaySum;
  const todayorderCount = graphData.todayCount;
  const yesterdayorderCount = graphData.yesterdayCount;
  const todayAverage  = (todayorderSum/todayorderCount).toFixed(2);
  const yesterdayAverage  = (yesterdayorderSum/yesterdayorderCount).toFixed(2);



  return loading ? (
    fourPlaceHolder()
  ) : (
    <div>
      <div>
        <AovSelectOption
          gender={gender}
          setGender={setGender}
          graphType={graphType}
          setGraphType={setGraphType}
          orderType={orderType}
          setOrderType={setOrderType}
        />
      </div>
      <div className="d-flex flex-row gap-4">
        <div>
          <div className="d-flex flex-column gap-3">
            <div
              className={`card shadow p-2 rounded-3 mt-4 cursor-pointer transition-all duration-300`}
            >
              <div className=" fw-bold mb-2 text-nowrap">Total Order Sum</div>
              <div className="fw-bold  text-dark mb-2 text-nowrap">
                {graphData.todaySum}{" "}
                <span className="fw-semibold text-muted ms-2">
                  {graphData.yesterdaySum}
                </span>
                <span className="ms-2">
                  {renderPercentChange(
                    graphData.todaySum,
                    graphData.yesterdaySum
                  )}
                </span>
              </div>
            </div>
            <div
              className={`card shadow p-2 rounded-3 cursor-pointer transition-all duration-300`}
            >
              <div className=" fw-bold mb-2">Total Order Count</div>
              <div className="fw-bold  text-dark mb-2 text-nowrap">
                {graphData.todayCount}{" "}
                <span className="fw-semibold text-muted ms-2">
                  {graphData.yesterdayCount}
                </span>
                <span className="ms-2">
                  {renderPercentChange(
                    graphData.todayCount,
                    graphData.yesterdayCount
                  )}
                </span>
              </div>
            </div>
            <div
              className={`card shadow p-2 rounded-3 cursor-pointer transition-all duration-300`}
            >
              <div className=" fw-bold mb-2">Average Order Value</div>
              <div className="fw-bold  text-dark mb-2 text-nowrap">
                {todayAverage}{" "}
                <span className="fw-semibold text-muted ms-2">
                  {yesterdayAverage}
                </span>
                <span className="ms-2">
                  {renderPercentChange(
                    todayAverage,
                    yesterdayAverage
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="flex-4 w-75">
          <ReactEChartsCore
            echarts={echarts}
            option={getOption(graphData.today, graphData.yesterday)}
            style={{ height: "25rem" }}
            className="rounded-4"
          />
        </div>
      </div>
    </div>
  );
}

export default AovDashboard;
